import { reloadNavigator } from './request';

const PIN_HEADER = 'x-eb-listings-page-origin';
const PIN_PARAMETER = 'listingsPageOrigin';
const BOOTSTRAP_PROPERTY = '__ebListingsPageOrigin';
const RELOAD_GUARD_KEY = '__ebListingsPageOriginReload';
const RELOAD_BUDGET_KEY = '__ebListingsPageOriginReloadBudget';
const RELOAD_WINDOW_MS = 60_000;
const MAX_RELOAD_ATTEMPTS = 2;
const NEW_ORIGIN_HEADER = 'x-eb-origin-id';
const NEW_ORIGIN_VALUE = 'NEW';
const EFFECTIVE_ORIGIN_HEADER = 'x-eb-listings-page-origin-effective';
const LISTINGS_API_PATH = /^\/(?:e\/api|api\/listings)(?:\/|$)/;

const rejectedDocuments = new WeakSet<object>();

function guardedToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch {
    return null;
  }
}

function registerReloadAttempt(token: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.sessionStorage.getItem(RELOAD_BUDGET_KEY);
    const previous = raw ? (JSON.parse(raw) as { attempts?: number; updatedAt?: number }) : null;
    const activeBudget =
      previous !== null &&
      Number.isSafeInteger(previous.attempts) &&
      typeof previous.updatedAt === 'number' &&
      Date.now() >= previous.updatedAt &&
      Date.now() - previous.updatedAt < RELOAD_WINDOW_MS;
    const attempts = activeBudget ? (previous!.attempts as number) : 0;
    if (attempts >= MAX_RELOAD_ATTEMPTS) return false;
    window.sessionStorage.setItem(
      RELOAD_BUDGET_KEY,
      JSON.stringify({ token, attempts: attempts + 1, updatedAt: Date.now() }),
    );
    return true;
  } catch {
    return false;
  }
}

function reloadOnce(token: string): void {
  if (typeof window === 'undefined') return;
  rejectedDocuments.add(window);
  try {
    if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === token) return;
  } catch {
    return;
  }
  if (!registerReloadAttempt(token)) return;
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, token);
  } catch {
    return;
  }
  reloadNavigator.reload();
}

function clearReloadGuard(): void {
  if (typeof window === 'undefined') return;
  if (rejectedDocuments.has(window)) return;
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {}
}

type PageOriginSnapshot = Readonly<{ token: string | null; armed: boolean }>;
type ListingsWindow = Window & { __ebListingsPageOrigin?: PageOriginSnapshot };

export function listingsPageOriginBootstrap(seamArmed = false): string {
  return (
    '(function(){if(Object.prototype.hasOwnProperty.call(window,"__ebListingsPageOrigin"))return;var armed=' +
    (seamArmed ? 'true' : 'false') +
    ';var token=null;var stale=null;if(armed){var pairs=[];try{pairs=document.cookie.split(";")}catch(e){}for(var i=0;i<pairs.length;i++){var pair=pairs[i].trim();if(pair.indexOf("listingsPageOrigin=")!==0)continue;var raw=pair.slice(19);try{raw=decodeURIComponent(raw)}catch(e){}var parts=raw.split("|");if(raw.length>512||!Number.isSafeInteger(Number(parts[2]))||parts.length!==4||parts[0]!=="new"||parts[1]!==window.location.hostname.toLowerCase()||!/^[0-9]+$/.test(parts[2])||!/^[0-9a-f]{64}$/.test(parts[3]))continue;if(Number(parts[2])>Date.now()/1000){token=raw;break}if(stale===null)stale=raw}if(token===null)token=stale}Object.defineProperty(window,"__ebListingsPageOrigin",{value:Object.freeze({token:token,armed:armed}),writable:false,configurable:false});})();'
  );
}

export class ListingsPageOriginExpiredError extends Error {
  constructor() {
    super('Reload this listings document before making another page request.');
    this.name = 'ListingsPageOriginExpiredError';
  }
}

function snapshot(): PageOriginSnapshot | null {
  if (typeof window === 'undefined') return null;
  return (window as ListingsWindow)[BOOTSTRAP_PROPERTY] ?? null;
}

function snapshotToken(): string | null {
  return snapshot()?.token ?? null;
}

export function hasListingsPageOriginPin(): boolean {
  return Boolean(snapshot()?.token);
}

export function isListingsPageOriginSeamArmed(): boolean {
  return snapshot()?.armed === true;
}

function missingPinKey(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  return `missing@${host}`;
}

function pageToken(): string | null {
  const token = snapshotToken();
  if (!token) {
    if (isListingsPageOriginSeamArmed()) {
      reloadOnce(missingPinKey());
      throw new ListingsPageOriginExpiredError();
    }
    return null;
  }
  const parts = token.split('|');
  if (parts[1] === window.location.hostname.toLowerCase() && Number(parts[2]) > Date.now() / 1000) {
    const guarded = guardedToken();
    if (guarded !== null && guarded !== token) clearReloadGuard();
    return token;
  }
  // A pin exists but is stale for this host/expiry: reload once to refresh it.
  reloadOnce(token);
  throw new ListingsPageOriginExpiredError();
}

export function withListingsPageOrigin(input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined {
  if (typeof window === 'undefined') return init;
  const request = typeof Request !== 'undefined' && input instanceof Request ? input : undefined;
  let url: URL;
  try {
    url = new URL(request ? request.url : String(input), window.location.href);
  } catch {
    return init;
  }
  if (url.origin !== window.location.origin || !LISTINGS_API_PATH.test(url.pathname)) return init;
  const token = pageToken();
  if (!token) return init;
  const headers = new Headers(init?.headers ?? request?.headers);
  headers.set(PIN_HEADER, token);
  return { ...init, headers };
}

export function listingsPageOriginNavigation(location: string): string {
  if (typeof window === 'undefined') return location;
  try {
    const url = new URL(location, window.location.href);
    if (url.origin !== window.location.origin || !/^\/e\/[^/]+\/protected\/?$/.test(url.pathname)) return location;
    const token = pageToken();
    if (!token) return location;
    url.searchParams.set(PIN_PARAMETER, token);
    return url.pathname + url.search + url.hash;
  } catch (error) {
    if (error instanceof ListingsPageOriginExpiredError) throw error;
    return location;
  }
}

function isListingsApiResponse(response: Response): boolean {
  if (!response.url) return false;
  try {
    const url = new URL(response.url, window.location.href);
    return url.origin === window.location.origin && LISTINGS_API_PATH.test(url.pathname);
  } catch {
    return false;
  }
}

export function checkListingsPageOriginResponse(response: Response): void {
  const token = snapshotToken();
  if (!token && !isListingsPageOriginSeamArmed()) return;
  if (!isListingsApiResponse(response)) return;
  const effective = response.headers.get(EFFECTIVE_ORIGIN_HEADER);
  const origin = response.headers.get(NEW_ORIGIN_HEADER);
  if (
    (response.status === 409 && response.headers.get('x-eb-listings-reload') === 'required') ||
    effective === 'old'
  ) {
    reloadOnce(token ?? missingPinKey());
    throw new ListingsPageOriginExpiredError();
  }
  if (response.ok && (effective === 'new' || (effective === null && origin === NEW_ORIGIN_VALUE))) {
    clearReloadGuard();
  }
}
